import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Modal,
  Button,
  Alert,
  FormGroup,
  ControlLabel,
  FormControl,
} from "react-bootstrap";
import { routes } from "@common/constants/urls";
import PROCESS_TYPES from "@common/constants/processTypes";
import { isDeviceKeg, isDeviceOffline } from "@util/deviceStatusHelper";
import * as deviceService from "@common/services/deviceService";
import { PHASES } from "@brewery/services/fermentationOrchestrator";
import {
  recipeFitsKeg,
  fermentationSummary,
} from "../EditFermentationModal/recipeFermentation";
import useFermentationOrchestrator from "@brewery/hooks/useFermentationOrchestrator";
import FermentationProgress from "@brewery/components/FermentationProgress/FermentationProgress";
import FermentationWarning from "@brewery/components/FermentationWarning/FermentationWarning";
import "./StartFermentationModal.scss";

export function StartFermentationModal({ show, handleClose, recipe }) {
  const history = useHistory();
  const [kegs, setKegs] = useState([]);
  const [selectedKegId, setSelectedKegId] = useState("");
  const [loadingKegs, setLoadingKegs] = useState(false);
  const [kegsError, setKegsError] = useState(null);

  const {
    phase,
    error,
    inFlight,
    startOrchestration,
    retry,
    resolveConflictAndRetry,
    reset,
  } = useFermentationOrchestrator();

  const fetchKegs = useCallback(async () => {
    setLoadingKegs(true);
    setKegsError(null);
    try {
      const allDevices = await deviceService.getDevices();
      const eligibleKegs = (allDevices || []).filter(
        (device) =>
          isDeviceKeg(device) &&
          !isDeviceOffline(device) &&
          device.process_type !== PROCESS_TYPES.PROC_CLEAN_KEG,
      );
      setKegs(eligibleKegs);

      // Auto-select first idle keg, or first keg if any
      if (eligibleKegs.length > 0) {
        const idleKeg = eligibleKegs.find(
          (k) =>
            !(k.session_id || k.active_session) &&
            k.process_type === PROCESS_TYPES.PROC_IDLE,
        );
        const defaultKeg = idleKeg || eligibleKegs[0];
        setSelectedKegId(defaultKeg.uuid || defaultKeg.serial_number);
      } else {
        setSelectedKegId("");
      }
    } catch (err) {
      setKegsError("Failed to load devices. Please check your connection.");
    } finally {
      setLoadingKegs(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      reset();
      fetchKegs();
    }
  }, [show, fetchKegs, reset]);

  const selectedKeg = kegs.find(
    (k) => (k.uuid || k.serial_number) === selectedKegId,
  );

  const kegSessionId = selectedKeg?.session_id || selectedKeg?.active_session;
  const isKegBusy =
    kegSessionId != null ||
    (selectedKeg?.process_type != null &&
      selectedKeg.process_type !== PROCESS_TYPES.PROC_IDLE);
  const mode = isKegBusy ? "swap" : "start";
  const eligibility = recipe
    ? recipeFitsKeg(recipe)
    : { ok: false, reason: "No recipe selected" };
  const scheduleRows = recipe ? fermentationSummary(recipe) : [];

  const handleStart = () => {
    if (!selectedKeg || !recipe || !eligibility.ok) return;

    startOrchestration({
      deviceUuid: selectedKeg.uuid || selectedKeg.serial_number,
      sessionId: kegSessionId,
      mode,
      beerRecipeId: recipe.id,
    });
  };

  const inProgress = phase !== null;

  const onModalClose = () => {
    if (!inFlight) {
      const wasDone = phase === PHASES.DONE;
      reset();
      handleClose();
      if (
        wasDone &&
        history &&
        history.location &&
        history.location.pathname !== routes.brewery
      ) {
        history.push(routes.brewery);
      }
    }
  };

  const recipeTitle = recipe
    ? `${recipe.beer_name || "Recipe"} ${recipe.version_name ? `(v${recipe.version_name})` : ""}`
    : "Recipe";

  return (
    <Modal
      show={show}
      onHide={inFlight ? () => {} : onModalClose}
      backdrop={inFlight ? "static" : true}
      className="start-fermentation-modal"
    >
      <Modal.Header closeButton={!inFlight}>
        <Modal.Title className="modal-title">
          START FERMENTATION &mdash; {recipeTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {inProgress ? (
          <FermentationProgress
            phase={phase}
            error={error}
            inFlight={inFlight}
            retry={retry}
            stopConflictAndRetry={resolveConflictAndRetry}
            handleClose={onModalClose}
          />
        ) : (
          <div className="start-fermentation-modal__content">
            {/* Recipe fermentation summary */}
            <div className="start-fermentation-modal__recipe-card">
              <h4 className="start-fermentation-modal__section-title">
                Fermentation Schedule
              </h4>
              {!eligibility.ok ? (
                <Alert bsStyle="danger" style={{ marginBottom: "15px" }}>
                  {eligibility.reason}
                </Alert>
              ) : (
                <div className="start-fermentation-modal__schedule">
                  <ul className="start-fermentation-modal__steps">
                    {scheduleRows.map((step, idx) => (
                      <li key={idx} className="start-fermentation-modal__step">
                        <span className="step-name">{step.stageName}:</span>{" "}
                        <strong>{step.temperature}</strong>
                        {step.endTemperature != null &&
                        step.endTemperature !== step.temperature
                          ? ` → ${step.endTemperature}`
                          : ""}{" "}
                        °C &middot;{" "}
                        <span>
                          {Number.isInteger(step.durationDays)
                            ? step.durationDays
                            : step.durationDays.toFixed(1)}{" "}
                          days
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="start-fermentation-modal__serving">
                    Serving Temperature:{" "}
                    <strong>{Number(recipe.serving_temperature)} °C</strong>
                  </div>
                  <p className="start-fermentation-modal__note">
                    Your keg will control temperature and dry-hopping prompts
                    according to this schedule.
                  </p>
                </div>
              )}
            </div>

            {/* Keg selection */}
            <div className="start-fermentation-modal__keg-select">
              <FormGroup controlId="kegSelectForm">
                <ControlLabel>Target MiniBrew Keg</ControlLabel>
                {loadingKegs ? (
                  <p className="text-muted">Loading available kegs&hellip;</p>
                ) : kegsError ? (
                  <Alert bsStyle="danger">{kegsError}</Alert>
                ) : kegs.length === 0 ? (
                  <Alert bsStyle="warning">
                    No available MiniBrew Kegs found. Make sure your Keg is
                    powered on and connected to Wi-Fi.
                  </Alert>
                ) : (
                  <FormControl
                    componentClass="select"
                    value={selectedKegId}
                    onChange={(e) => setSelectedKegId(e.target.value)}
                    className="start-fermentation-modal__select"
                  >
                    {kegs.map((keg) => {
                      const id = keg.uuid || keg.serial_number;
                      const name =
                        keg.custom_name || keg.title || keg.serial_number;
                      const isBusy = !!(
                        keg.session_id ||
                        keg.active_session ||
                        (keg.process_type != null &&
                          keg.process_type !== PROCESS_TYPES.PROC_IDLE)
                      );
                      const statusLabel = isBusy
                        ? " (Active Session — Will Replace)"
                        : " (Idle)";
                      return (
                        <option key={id} value={id}>
                          {name}
                          {statusLabel}
                        </option>
                      );
                    })}
                  </FormControl>
                )}
              </FormGroup>
            </div>

            {selectedKeg && <FermentationWarning mode={mode} />}

            <div className="start-fermentation-modal__actions">
              <Button
                bsStyle="primary"
                bsSize="large"
                block
                className="start-fermentation-modal__btn-submit"
                disabled={!selectedKeg || !eligibility.ok || loadingKegs}
                onClick={handleStart}
              >
                {mode === "swap" ? "APPLY TO KEG" : "START FERMENTATION"}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

StartFermentationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  recipe: PropTypes.object,
};

export default StartFermentationModal;
